import { QueryKey, keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiError } from 'src/types/interfaces/api-error';

import { MEILISEARCH_ERRORS_CODES_TO_IGNORE } from './constants';
import { MeilisearchError } from './types/meilisearch-error';
import { MeiliSearchResponse } from './types/meilisearch-response';
import { SearchState } from './types/search-state';

export interface useMeilisearchReturn<TData> {
  results: MeiliSearchResponse<TData> | undefined;
  isLoading: boolean;
  error: ApiError | null;
  isFetching: boolean;
}

const defaultSearchState: SearchState = {
  filter: [],
  q: '',
  sort: [],
};

interface MeilisearchPayload<TData, TQueryKey> {
  fetch: (
    searchState: SearchState,
    page: number,
    perPage: number,
  ) => Promise<MeiliSearchResponse<TData>>;
  page: number;
  perPage: number;
  queryKey?: TQueryKey;
  searchState?: SearchState;
  enabled?: boolean;
}

export function useMeilisearch<TData, TQueryKey extends QueryKey = QueryKey>({
  fetch,
  page,
  perPage,
  queryKey,
  searchState,
  enabled = true,
}: MeilisearchPayload<TData, TQueryKey>): useMeilisearchReturn<TData> {
  const { data, isLoading, error, isFetching } = useQuery<
    MeiliSearchResponse<TData>,
    MeilisearchError
  >({
    queryKey: [...(queryKey ?? ''), { searchState, page, perPage }],
    queryFn: () => fetch(searchState ?? defaultSearchState, page, perPage),
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    results: data,
    isFetching,
    isLoading,
    error: shouldIgnoreError(error),
  };
}

const shouldIgnoreError = (error: MeilisearchError | null) => {
  if (!error) {
    return error;
  }

  const shouldIgnore = MEILISEARCH_ERRORS_CODES_TO_IGNORE.includes(error.response?.data?.code);

  if (shouldIgnore) {
    return null;
  }

  return error;
};
